import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["item", "grid"]
  static values = { dashboardId: Number, editing: { type: Boolean, default: false } }

  connect() {
    // Snapshot positions when entering edit mode, for reverting on cancel
    this._savedPositions = null
    this._pendingDeletions = []
    this._onGlobalToggle = () => this.toggleEdit()
    window.addEventListener("dashboard:toggle-edit", this._onGlobalToggle)
  }

  disconnect() {
    window.removeEventListener("dashboard:toggle-edit", this._onGlobalToggle)
    if (this._dragCleanup) this._dragCleanup()
  }

  toggleEdit() {
    if (this.editingValue) {
      // Clicking "Done" — persist all changes and exit edit mode
      this._persistAllChanges()
      this.editingValue = false
    } else {
      // Entering edit mode — snapshot current positions and reset pending deletions
      this._savedPositions = this.itemTargets.map(el => ({
        el,
        id: el.dataset.itemId,
        ...this._getItemRect(el)
      }))
      this._pendingDeletions = []
      this.editingValue = true
      this._compactAll()
    }
    this.applyEditMode()
  }

  interceptDelete(event) {
    if (!this.editingValue) return
    event.preventDefault()
    const item = event.target.closest("[data-item-id]")
    if (!item) return
    this._pendingDeletions.push({
      id: item.dataset.itemId,
      url: `/analytics/dashboards/${this.dashboardIdValue}/items/${item.dataset.itemId}`
    })
    item.remove()
  }

  applyEditMode() {
    const grid = this.gridTarget
    const btn = document.querySelector("[data-edit-btn]")

    if (this.editingValue) {
      grid.classList.add("dashboard-grid-editing")
      this.itemTargets.forEach((item) => {
        item.classList.add("dashboard-grid-item-editing")
      })
      if (btn) {
        btn.textContent = "Done"
        btn.classList.remove("btn-hollow")
        btn.classList.add("btn-primary-sm")
      }
    } else {
      grid.classList.remove("dashboard-grid-editing")
      this.itemTargets.forEach((item) => {
        item.classList.remove("dashboard-grid-item-editing")
      })
      if (btn) {
        btn.textContent = "Rearrange"
        btn.classList.add("btn-hollow")
        btn.classList.remove("btn-primary-sm")
      }
    }
  }

  startMove(event) {
    if (!this.editingValue) return
    const target = event.target
    if (target.closest(".resize-handle, button, input, form")) return
    event.preventDefault()
    this._startDrag(event, "move")
  }

  startResize(event) {
    if (!this.editingValue) return
    event.preventDefault()
    event.stopPropagation()
    const handle = event.target.closest("[data-resize-dir]")
    const dir = handle?.dataset.resizeDir || "se"
    if (handle) handle.classList.add("resizing")
    this._startDrag(event, dir, handle)
  }

  _getItemRect(el) {
    const col = parseInt(el.style.gridColumn.split(" / ")[0]) || 1
    const row = parseInt(el.style.gridRow.split(" / ")[0]) || 1
    const w = parseInt(el.style.gridColumn.split("span ")[1]) || 6
    const h = parseInt(el.style.gridRow.split("span ")[1]) || 4
    return { col, row, w, h, el }
  }

  _rectsOverlap(a, b) {
    return a.col < b.col + b.w && a.col + a.w > b.col &&
           a.row < b.row + b.h && a.row + a.h > b.row
  }

  // Compact all items upward to fill gaps (no dragged item)
  _compactAll() {
    const items = this.itemTargets.map(el => this._getItemRect(el))
    items.sort((a, b) => a.row - b.row)
    for (const item of items) {
      let bestRow = 1
      while (bestRow < item.row) {
        const candidate = { col: item.col, row: bestRow, w: item.w, h: item.h }
        const blocked = items.some(o => o !== item && this._rectsOverlap(candidate, o))
        if (!blocked) { item.row = bestRow; break }
        bestRow++
      }
    }
    for (const item of items) {
      item.el.style.gridColumn = `${item.col} / span ${item.w}`
      item.el.style.gridRow = `${item.row} / span ${item.h}`
    }
  }

  // Push overlapping items down, then compact everything upward
  _pushItemsDown(draggedItem, col, row, w, h) {
    const dragged = { col, row, w, h }

    const others = this.itemTargets
      .filter(el => el !== draggedItem)
      .map(el => this._getItemRect(el))

    // Resolve collisions: push overlapping items below the dragged item
    let changed = true
    let iterations = 0
    while (changed && iterations < 50) {
      changed = false
      iterations++
      for (const item of others) {
        if (this._rectsOverlap(dragged, item)) {
          item.row = dragged.row + dragged.h
          changed = true
        }
        for (const other of others) {
          if (other === item) continue
          if (this._rectsOverlap(item, other)) {
            const lower = item.row >= other.row ? item : other
            const upper = lower === item ? other : item
            lower.row = upper.row + upper.h
            changed = true
          }
        }
      }
    }

    // Compact: move each item as high as possible without overlapping
    others.sort((a, b) => a.row - b.row)
    for (const item of others) {
      let bestRow = 1
      while (bestRow < item.row) {
        const candidate = { col: item.col, row: bestRow, w: item.w, h: item.h }
        const blocked = this._rectsOverlap(dragged, candidate) ||
          others.some(o => o !== item && this._rectsOverlap(candidate, o))
        if (!blocked) { item.row = bestRow; break }
        bestRow++
      }
    }

    // Apply new positions to DOM
    for (const item of others) {
      item.el.style.gridColumn = `${item.col} / span ${item.w}`
      item.el.style.gridRow = `${item.row} / span ${item.h}`
    }
  }

  _startDrag(event, mode, activeHandle = null) {
    const item = event.target.closest("[data-item-id]")
    if (!item) return

    const grid = this.gridTarget
    const colWidth = grid.offsetWidth / 12
    const rowHeight = 80 + 16

    const startMouseX = event.clientX
    const startMouseY = event.clientY

    const { col: startCol, row: startRow, w: startW, h: startH } = this._getItemRect(item)

    // Snapshot positions of other items for reset during this drag
    const originalPositions = this.itemTargets
      .filter(el => el !== item)
      .map(el => ({ el, ...this._getItemRect(el) }))

    let lastCol = startCol, lastRow = startRow, lastW = startW, lastH = startH

    item.style.zIndex = "10"
    item.style.opacity = "0.9"
    document.body.style.userSelect = "none"
    const preventSelect = (e) => e.preventDefault()
    document.addEventListener("selectstart", preventSelect)

    // Auto-scroll state — the actual scrollable element is the layout's content div,
    // not window (body is overflow-hidden, content div is overflow-y-scroll)
    const scrollContainer = grid.closest(".overflow-y-scroll") || grid.closest(".overflow-y-auto") || document.scrollingElement
    let currentClientX = startMouseX
    let currentClientY = startMouseY
    const scrollAtStart = scrollContainer.scrollTop
    const EDGE_ZONE = 60
    const MAX_SCROLL_SPEED = 12

    const recalcPosition = () => {
      const scrollDelta = scrollContainer.scrollTop - scrollAtStart
      const dx = currentClientX - startMouseX
      const dy = (currentClientY - startMouseY) + scrollDelta
      const dCols = Math.round(dx / colWidth)
      const dRows = Math.round(dy / rowHeight)

      let col, row, w, h

      if (mode === "move") {
        col = Math.max(1, Math.min(13 - startW, startCol + dCols))
        row = Math.max(1, startRow + dRows)
        w = startW
        h = startH
      } else {
        col = startCol
        row = startRow
        w = startW
        h = startH

        if (mode === "se" || mode === "ne") {
          w = Math.max(2, Math.min(13 - startCol, startW + dCols))
        }
        if (mode === "sw" || mode === "nw") {
          const newCol = Math.max(1, Math.min(startCol + startW - 2, startCol + dCols))
          w = startW + (startCol - newCol)
          col = newCol
        }
        if (mode === "se" || mode === "sw") {
          h = Math.max(2, startH + dRows)
        }
        if (mode === "ne" || mode === "nw") {
          const newRow = Math.max(1, Math.min(startRow + startH - 2, startRow + dRows))
          h = startH + (startRow - newRow)
          row = newRow
        }
      }

      if (col === lastCol && row === lastRow && w === lastW && h === lastH) return

      // Reset other items to pre-drag positions before recalculating pushes
      for (const pos of originalPositions) {
        pos.el.style.gridColumn = `${pos.col} / span ${pos.w}`
        pos.el.style.gridRow = `${pos.row} / span ${pos.h}`
      }

      lastCol = col
      lastRow = row
      lastW = w
      lastH = h
      item.style.gridColumn = `${col} / span ${w}`
      item.style.gridRow = `${row} / span ${h}`
      this._pushItemsDown(item, col, row, w, h)
    }

    // Auto-scroll when dragging near the scroll container's edges
    const scrollInterval = setInterval(() => {
      const rect = scrollContainer.getBoundingClientRect()
      let speed = 0
      if (currentClientY < rect.top + EDGE_ZONE) {
        speed = -MAX_SCROLL_SPEED * (1 - (currentClientY - rect.top) / EDGE_ZONE)
      } else if (currentClientY > rect.bottom - EDGE_ZONE) {
        speed = MAX_SCROLL_SPEED * (1 - (rect.bottom - currentClientY) / EDGE_ZONE)
      }
      if (speed !== 0) {
        scrollContainer.scrollBy(0, speed)
        recalcPosition()
      }
    }, 16)

    const onMouseMove = (e) => {
      currentClientX = e.clientX
      currentClientY = e.clientY
      recalcPosition()
    }

    const cleanup = () => {
      clearInterval(scrollInterval)
      document.removeEventListener("mousemove", onMouseMove)
      document.removeEventListener("mouseup", onMouseUp)
      document.removeEventListener("selectstart", preventSelect)
      document.body.style.userSelect = ""
      if (activeHandle) activeHandle.classList.remove("resizing")
      item.style.zIndex = ""
      item.style.opacity = ""
      this._dragCleanup = null
    }

    const onMouseUp = () => cleanup()

    this._dragCleanup = cleanup
    document.addEventListener("mousemove", onMouseMove)
    document.addEventListener("mouseup", onMouseUp)
  }

  _persistAllChanges() {
    if (!this._savedPositions) return

    const token = document.querySelector("meta[name=csrf-token]")?.content

    for (const el of this.itemTargets) {
      const current = this._getItemRect(el)
      const saved = this._savedPositions.find(p => p.el === el)
      if (!saved) continue
      if (current.col === saved.col && current.row === saved.row &&
          current.w === saved.w && current.h === saved.h) continue

      const url = `/analytics/dashboards/${this.dashboardIdValue}/items/${el.dataset.itemId}`
      const body = new URLSearchParams({
        x: current.col - 1, y: current.row - 1, w: current.w, h: current.h
      })
      fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/x-www-form-urlencoded", "X-CSRF-Token": token },
        body,
      })
    }
    this._savedPositions = null

    for (const { url } of this._pendingDeletions) {
      fetch(url, {
        method: "DELETE",
        headers: { "X-CSRF-Token": token }
      })
    }
    this._pendingDeletions = []
  }
}
